import { useEffect, useState } from 'react';

const COINGECKO_API_KEY =
    process.env.REACT_APP_COINGECKO_API_KEY || 'CG-AMT6oz8K7Qpx7qESamW9hKZu';

// Fallback prices and contract addresses
const LEIA_FALLBACK_PRICE = 0.0001743;
const LEIA_CONTRACT_ADDRESS = '0x9F24818c84B357Ee05CFB2F2c1537C73c7Cb1502';

const DOP_MIN_ATH = 0.1143;
const DOP_CONTRACT_ADDRESS = '0xa37DB5456b5938Aa50B33575Ae2eB8048CA5959b';

// RAIN token with shorter cache duration
const RAIN_TOKEN_ADDRESS = '0x25118290e6A5f4139381D072181157035864099d';
const RAIN_TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache configuration
const CACHE_DURATION = 12 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_PREFIX = 'token_ath_';

/**
 * Safely checks if localStorage is available and working
 */
const isLocalStorageAvailable = () => {
    try {
        const test = '__storage_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * In-memory fallback cache for when localStorage is unavailable
 */
const memoryCache = new Map();

/**
 * Determines the cache duration for a given token
 */
const getCacheDuration = (contractAddress) => {
    if (contractAddress.toLowerCase() === RAIN_TOKEN_ADDRESS.toLowerCase()) {
        return RAIN_TOKEN_CACHE_DURATION;
    }
    return CACHE_DURATION;
};

/**
 * Gets cached data from localStorage or memory cache
 */
const getCachedData = (key, contractAddress) => {
    try {
        const cacheDuration = getCacheDuration(contractAddress);

        if (isLocalStorageAvailable()) {
            const cached = localStorage.getItem(CACHE_PREFIX + key);
            if (cached) {
                const { price, timestamp } = JSON.parse(cached);
                const now = Date.now();

                // Check if cache is still valid
                if (now - timestamp < cacheDuration) {
                    return price;
                } else {
                    // Cache expired, remove it
                    localStorage.removeItem(CACHE_PREFIX + key);
                }
            }
        } else {
            // Fallback to memory cache
            const cached = memoryCache.get(key);
            if (cached) {
                const { price, timestamp } = cached;
                const now = Date.now();

                if (now - timestamp < cacheDuration) {
                    return price;
                } else {
                    memoryCache.delete(key);
                }
            }
        }
    } catch (error) {
        console.warn('Error reading cache:', error);
    }
    return null;
};

/**
 * Sets cached data in localStorage or memory cache
 */
const setCachedData = (key, price) => {
    try {
        const cacheData = {
            price,
            timestamp: Date.now()
        };

        if (isLocalStorageAvailable()) {
            localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
        } else {
            // Fallback to memory cache
            memoryCache.set(key, cacheData);
        }
    } catch (error) {
        console.warn('Error setting cache:', error);
        // If localStorage is full, try memory cache as fallback
        try {
            memoryCache.set(key, {
                price,
                timestamp: Date.now()
            });
        } catch (e) {
            console.warn('Memory cache also failed:', e);
        }
    }
};

/**
 * Fetches the all-time high (ATH) USD price for a given token on a specific EVM chain.
 * @param {string} platform - The platform id (e.g. 'ethereum', 'binance-smart-chain', 'polygon-pos')
 * @param {string} contractAddress - The token's contract address
 * @returns {{ price: number|null, loading: boolean, error: string|null }}
 */
export const useTokenATH = (platform, contractAddress) => {
    const [price, setPrice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!platform || !contractAddress) {
            setLoading(false);
            return;
        }

        // Create a unique cache key combining platform and contract address
        const cacheKey = `${platform}_${contractAddress.toLowerCase()}`;

        // Check if this is LEIA or DOP token
        const isLeiaToken = contractAddress.toLowerCase() === LEIA_CONTRACT_ADDRESS.toLowerCase();
        const isDopToken = contractAddress.toLowerCase() === DOP_CONTRACT_ADDRESS.toLowerCase();

        const fetchATH = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check cache first
                const cachedPrice = getCachedData(cacheKey, contractAddress);
                if (cachedPrice !== null) {
                    setPrice(cachedPrice);
                    setLoading(false);
                    return;
                }

                // No valid cache, fetch from API
                const response = await fetch(
                    `https://pro-api.coingecko.com/api/v3/coins/${platform}/contract/${contractAddress}`,
                    {
                        headers: { 'x-cg-pro-api-key': COINGECKO_API_KEY },
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                const ath = data.market_data?.ath?.usd ?? null;

                let finalPrice = null;

                // Use fallback price for LEIA if API returns null
                if (isLeiaToken && !ath) {
                    finalPrice = LEIA_FALLBACK_PRICE;
                } else if (isDopToken && ath) {
                    // For DOP, use ATH if greater than 0.1143, otherwise use 0.1143
                    finalPrice = ath > DOP_MIN_ATH ? ath : DOP_MIN_ATH;
                } else if (isDopToken && !ath) {
                    // If DOP API returns null, use minimum ATH
                    finalPrice = DOP_MIN_ATH;
                } else {
                    finalPrice = ath;
                }

                // Cache the result
                if (finalPrice !== null) {
                    setCachedData(cacheKey, finalPrice);
                }

                setPrice(finalPrice);
            } catch (err) {
                console.error('Error fetching ATH:', err);
                setError(err.message);

                // Use fallback price for LEIA even on error
                if (isLeiaToken) {
                    const fallbackPrice = LEIA_FALLBACK_PRICE;
                    setPrice(fallbackPrice);
                    setCachedData(cacheKey, fallbackPrice);
                } else if (isDopToken) {
                    const fallbackPrice = DOP_MIN_ATH;
                    setPrice(fallbackPrice);
                    setCachedData(cacheKey, fallbackPrice);
                } else {
                    setPrice(null);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchATH();
    }, [platform, contractAddress]);

    return { price, loading, error };
};

/**
 * Utility function to manually clear cache (optional, for debugging or user action)
 */
export const clearTokenCache = (platform = null, contractAddress = null) => {
    try {
        if (platform && contractAddress) {
            // Clear specific token
            const cacheKey = `${platform}_${contractAddress.toLowerCase()}`;
            if (isLocalStorageAvailable()) {
                localStorage.removeItem(CACHE_PREFIX + cacheKey);
            }
            memoryCache.delete(cacheKey);
        } else {
            // Clear all token caches
            if (isLocalStorageAvailable()) {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith(CACHE_PREFIX)) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
            }
            memoryCache.clear();
        }
    } catch (error) {
        console.warn('Error clearing cache:', error);
    }
};
// import { useEffect, useState } from 'react';

// const COINGECKO_API_KEY =
//     process.env.REACT_APP_COINGECKO_API_KEY || 'CG-AMT6oz8K7Qpx7qESamW9hKZu';

// // Fallback prices and contract addresses
// const LEIA_FALLBACK_PRICE = 0.0001743;
// const LEIA_CONTRACT_ADDRESS = '0x9F24818c84B357Ee05CFB2F2c1537C73c7Cb1502';

// const DOP_MIN_ATH = 0.1143;
// const DOP_CONTRACT_ADDRESS = '0xa37DB5456b5938Aa50B33575Ae2eB8048CA5959b';

// /**
//  * Fetches the all-time high (ATH) USD price for a given token on a specific EVM chain.
//  * @param {string} platform - The platform id (e.g. 'ethereum', 'binance-smart-chain', 'polygon-pos')
//  * @param {string} contractAddress - The token's contract address
//  * @returns {{ price: number|null, loading: boolean, error: string|null }}
//  */
// export const useTokenATH = (platform, contractAddress) => {
//     const [price, setPrice] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);

//     useEffect(() => {
//         if (!platform || !contractAddress) return;

//         // Check if this is LEIA or DOP token
//         const isLeiaToken = contractAddress.toLowerCase() === LEIA_CONTRACT_ADDRESS.toLowerCase();
//         const isDopToken = contractAddress.toLowerCase() === DOP_CONTRACT_ADDRESS.toLowerCase();

//         const fetchATH = async () => {
//             try {
//                 setLoading(true);
//                 setError(null);

//                 const response = await fetch(
//                     `https://pro-api.coingecko.com/api/v3/coins/${platform}/contract/${contractAddress}`,
//                     {
//                         headers: { 'x-cg-pro-api-key': COINGECKO_API_KEY },
//                     }
//                 );

//                 if (!response.ok) {
//                     throw new Error(`HTTP ${response.status}`);
//                 }

//                 const data = await response.json();
//                 const ath = data.market_data?.ath?.usd ?? null;

//                 // Use fallback price for LEIA if API returns null
//                 if (isLeiaToken && !ath) {
//                     setPrice(LEIA_FALLBACK_PRICE);
//                 } else if (isDopToken && ath) {
//                     // For DOP, use ATH if greater than 0.1143, otherwise use 0.1143
//                     setPrice(ath > DOP_MIN_ATH ? ath : DOP_MIN_ATH);
//                 } else if (isDopToken && !ath) {
//                     // If DOP API returns null, use minimum ATH
//                     setPrice(DOP_MIN_ATH);
//                 } else {
//                     setPrice(ath);
//                 }
//             } catch (err) {
//                 console.error('Error fetching ATH:', err);
//                 setError(err.message);

//                 // Use fallback price for LEIA even on error
//                 if (isLeiaToken) {
//                     setPrice(LEIA_FALLBACK_PRICE);
//                 } else if (isDopToken) {
//                     setPrice(DOP_MIN_ATH);
//                 } else {
//                     setPrice(null);
//                 }
//             } finally {
//                 setLoading(false);
//             }
//         };

//         fetchATH();
//     }, [platform, contractAddress]);

//     return { price, loading, error };
// };